import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, PhoneCall, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TextField, AreaField, SelectField } from "@/components/forms/Field";
import { AddressField } from "@/components/forms/AddressField";
import { FINANCING_TYPES, US_STATES, CREDIT_RANGES } from "@/data/site";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TOP = ["first_name", "last_name", "email", "phone", "company", "property_address", "property_state"];
const selectCls = "w-full h-11 rounded-md bg-surface/70 border border-edge text-white px-3 text-sm";

function Block({ title, children }) {
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-6 mt-5">
      <div className="eyebrow mb-4">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Done({ res, onAgain }) {
  const navigate = useNavigate();
  return (
    <div className="card-surface rounded-2xl p-8 max-w-xl text-center mx-auto mt-6" data-testid="intake-success">
      <CheckCircle2 className="w-10 h-10 text-ok mx-auto" />
      <h2 className="h-display text-2xl text-white mt-3">Deal saved</h2>
      <p className="font-mono text-gold text-lg mt-2" data-testid="intake-reference">{res.reference}</p>
      <p className="text-sm text-dim mt-2">
        {res.assigned_name ? `Assigned to ${res.assigned_name}. ` : "Not assigned yet. "}
        {res.invite_sent ? "The borrower was emailed a confirmation and a portal invite."
          : res.confirmation_sent ? "The borrower was emailed a confirmation." : "The borrower email could not be sent right now — follow up by phone or message."}
      </p>
      <div className="flex flex-col sm:flex-row gap-2 justify-center mt-6">
        <Button onClick={() => navigate(`/admin/${res.type}`)} className="btn-gold rounded-lg" data-testid="intake-open-files">Open My Files</Button>
        <Button onClick={onAgain} variant="outline" className="btn-outline-glass rounded-lg" data-testid="intake-new">Take Another Deal</Button>
      </div>
    </div>
  );
}

export default function AdminIntake() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState({ financing_type: "fix-and-flip" });
  const [invite, setInvite] = useState(true);
  const [staff, setStaff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const set = (n, v) => setF((s) => ({ ...s, [n]: v }));
  useEffect(() => { if (isAdmin) api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => setStaff([])); }, [isAdmin]);

  const kind = FINANCING_TYPES.find((t) => t.value === f.financing_type)?.kind || "loan";
  const isDSCR = f.financing_type === "dscr-rental";

  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email || !f.phone) { toast.error("Borrower name, email and phone are required"); return; }
    setBusy(true);
    const data = { ...f };
    TOP.concat(["assigned_to", "intake_note"]).forEach((k) => delete data[k]);
    try {
      const { data: r } = await api.post("/admin/intake", {
        type: kind, applicant_name: `${f.first_name} ${f.last_name}`.trim(), email: f.email, phone: f.phone,
        company: f.company || "", property_address: f.property_address || "", property_state: f.property_state || "",
        data, documents: [], assigned_to: f.assigned_to || null, invite_borrower: invite, intake_note: f.intake_note || "",
      });
      setRes(r);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (res) return <Done res={res} onAgain={() => { setRes(null); setF({ financing_type: "fix-and-flip" }); }} />;

  return (
    <div data-testid="admin-intake" className="max-w-4xl">
      <h1 className="h-display text-3xl text-white flex items-center gap-3"><PhoneCall className="w-7 h-7 text-gold" /> New Phone Deal</h1>
      <p className="mt-1 text-dim">Enter a deal while you're on the phone with the borrower.</p>

      <Block title="Program">
        <label className="text-sm font-medium text-white/80">Financing Type
          <select value={f.financing_type} onChange={(e) => set("financing_type", e.target.value)} className={`${selectCls} mt-1.5`} data-testid="intake-financing-type">
            {FINANCING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        {isAdmin && (
          <label className="text-sm font-medium text-white/80">Assign To
            <select value={f.assigned_to || ""} onChange={(e) => set("assigned_to", e.target.value)} className={`${selectCls} mt-1.5`} data-testid="intake-assign">
              <option value="">{staff ? "Leave unassigned" : "Loading team…"}</option>
              {(staff || []).map((s) => <option key={s.user_id} value={s.user_id}>{s.name || s.email} · {s.role.replace("_", " ")}</option>)}
            </select>
          </label>
        )}
      </Block>

      <Block title="Borrower">
        <TextField label="First Name" name="first_name" value={f.first_name} onChange={set} required testId="intake-first-name" />
        <TextField label="Last Name" name="last_name" value={f.last_name} onChange={set} required testId="intake-last-name" />
        <TextField label="Email" name="email" type="email" value={f.email} onChange={set} required testId="intake-email" />
        <TextField label="Phone" name="phone" value={f.phone} onChange={set} required testId="intake-phone" />
        <TextField label="Date of Birth" name="date_of_birth" type="date" value={f.date_of_birth} onChange={set} testId="intake-dob" />
        <TextField label="Borrowing Entity" name="borrowing_entity" value={f.borrowing_entity} onChange={set} testId="intake-entity" />
        <SelectField label="Credit Range" name="credit_range" value={f.credit_range} onChange={set} options={CREDIT_RANGES} testId="intake-credit" />
        <TextField label="Completed Investment Deals" name="completed_deals" type="number" value={f.completed_deals} onChange={set} testId="intake-deals" />
        <TextField label="Estimated Liquidity" name="liquidity" type="number" value={f.liquidity} onChange={set} testId="intake-liquidity" />
      </Block>

      <Block title="Property & Deal">
        <AddressField label="Property Address" value={f.property_address} onChange={(v) => set("property_address", v)} onSelect={(a) => a.state && set("property_state", a.state)} className="sm:col-span-2" testId="intake-property-address" />
        <SelectField label="Property State" name="property_state" value={f.property_state} onChange={set} options={US_STATES} testId="intake-property-state" />
        <TextField label="Property Type" name="property_type" value={f.property_type} onChange={set} testId="intake-property-type" />
        <TextField label="Purchase Price" name="purchase_price" type="number" value={f.purchase_price} onChange={set} testId="intake-purchase-price" />
        <TextField label="Loan Amount Requested" name="loan_amount" type="number" value={f.loan_amount} onChange={set} testId="intake-loan-amount" />
        <TextField label="As-Is Value" name="current_value" type="number" value={f.current_value} onChange={set} testId="intake-current-value" />
        {isDSCR ? (
          <TextField label="Monthly Rent" name="monthly_rent" type="number" value={f.monthly_rent} onChange={set} testId="intake-monthly-rent" />
        ) : (<>
          <TextField label="After Repair Value" name="arv" type="number" value={f.arv} onChange={set} testId="intake-arv" />
          <TextField label="Rehab Budget" name="rehab_budget" type="number" value={f.rehab_budget} onChange={set} testId="intake-rehab-budget" />
        </>)}
        <TextField label="Closing Date" name="closing_date" type="date" value={f.closing_date} onChange={set} testId="intake-closing-date" />
        <AreaField label="Call Notes (internal)" name="intake_note" value={f.intake_note} onChange={set} className="sm:col-span-2" testId="intake-notes" />
      </Block>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <label className="flex items-center gap-2 text-sm text-white/85 cursor-pointer">
          <Checkbox checked={invite} onCheckedChange={(v) => setInvite(!!v)} data-testid="intake-invite" />
          Email the borrower a portal invite (they also get a confirmation)
        </label>
        <Button onClick={submit} disabled={busy || (isAdmin && !staff)} className="btn-gold rounded-full h-12 px-8" data-testid="intake-submit">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Deal"}
        </Button>
      </div>
    </div>
  );
}
